<script lang="ts" setup>
import { useProductCardData } from "~/composables/ProductCardData"
import type { Product } from "~/models/Product"
import Rating from "~/components/ShowRating.vue"
import type { Category } from "~/models/Category"
import { capitalizeFirstLetter } from "~/common/helpers"

/**
 *
 *  Props and emits section
 *
 *
 */

const props = defineProps<{
    product: Product
}>()

/**
 *
 *  Ref, computed and hooks section
 *
 *
 */

const { slide, ranting, quantity, add, show } = useProductCardData(
    props.product
)

const loading = ref(false)

/**
 *
 *  Life cicle events
 *
 *
 */

/**
 *
 *  Functions section
 *
 *
 */

// TODO update this
function filterByBrand($id) {
    // window.location.href = route('store.brand', { id: $id });
}

const getImageSource = (image: any) => {
    if (image && "id" in image) {
        return image.type === "image/avif" ? image.origin : image.thumb
    } else {
        return ""
    }
}

const getImageId = (image: any) => {
    if (image && "id" in image) {
        return image.id
    } else {
        return ""
    }
}

const getImageStyle = (image: string) => {
    const imageObject = new Image()
    imageObject.src = image
    // if (imageObject.width > 260 || imageObject.height > 190) {
    //     return {
    //         backgroundSize: "contain",
    //         backgroundPosition: "center center",
    //     }
    // }
    return {
        backgroundSize: "contain",
        backgroundPosition: "center center",
    }
}

const onUpdateAmount = (amount: number) => {
    quantity.value = amount
}

const onAddToCart = (amount: number) => {
    loading.value = true
    add(props.product, amount)
        .then(() => {
            loading.value = false
        })
        .catch(() => {
            loading.value = false
        })
}
const showProduct = () => show(props.product)
</script>
<template>
    <div class="tw-flex tw-w-full tw-h-full card-product-container">
        <div
            class="tw-bg-white tw-cursor-pointer tw-rounded-md tw-relative tw-border tw-w-full tw-h-full"
        >
            <div
                class="tw-grid tw-gap-4 tw-grid-cols-1 md:tw-grid-cols-2 tw-h-full"
            >
                <div class="tw-flex tw-justify-center tw-h-full tw-relative">
                    <q-carousel
                        class="tw-w-full tw-cursor-pointer tw-rounded-[3px] tw-h-full card-product-image-container"
                        v-model="slide"
                        infinite
                        autoplay
                        animated
                        transition-next="fade"
                        transition-prev="fade"
                        @click="showProduct"
                    >
                        <q-carousel-slide
                            v-if="!product.images?.length"
                            name="-1"
                            img-src="/img/product_placeholder.png"
                            class="card_product_image tw-bg-cover tw-bg-center"
                        />

                        <q-carousel-slide
                            class="card_product_image"
                            v-for="(image, i) in product.images"
                            :key="i"
                            :name="getImageId(image)"
                            :img-src="getImageSource(image)"
                            :style="getImageStyle(image.thumb)"
                        />
                    </q-carousel>
                    <q-icon
                        name="visibility"
                        @click="showProduct"
                        class="tw-w-5 tw-h-5 tw-absolute tw-top-2 tw-right-5 tw-text-customGray-600 !tw-hidden button_card_product"
                    />
                </div>

                <div
                    class="tw-p-4 tw-relative tw-flex tw-flex-col tw-gap-2 md:tw-gap-4 tw-justify-between tw-h-full"
                >
                    <div class="countdown-mobile-container">
                        <CountDown :product="product" />
                    </div>
                    <p class="tw-text-customGray-300 tw-text-xs">
                        <span class="tw-font-bold">{{
                            product.store_name
                        }}</span>
                        <span
                            class="tw-font-bold"
                            v-if="
                                product.categories &&
                                product.categories.length > 0
                            "
                            >/</span
                        >
                        {{
                            (product.categories as Category[])
                                ?.map((i) => capitalizeFirstLetter(i.name))
                                .join(", ")
                        }}
                    </p>

                    <div class="tw-flex tw-flex-col tw-gap-3 tw-h-full">
                        <p
                            class="tw-text-xl tw-text-primary tw-font-semibold tw-my-0"
                        >
                            {{ capitalizeFirstLetter(product.name) }}
                        </p>
                        <span
                            v-if="
                                product.is_warranty &&
                                product.warranty_formatted
                            "
                            class="tw-text-sm tw-text-secondary"
                            >{{ product?.warranty_formatted }}</span
                        >
                        <span
                            v-if="product.brand"
                            class="tw-text-sm lg:tw-text-base tw-text-zinc-600 tw-font-semibold"
                        >
                            {{ product.brand }}</span
                        >
                        <Rating
                            :rating="product.rating_approved"
                            :valoration="product.reviews_approved"
                            color="secondary"
                            size="1.5rem"
                        />
                        <ProductPrice :product="product" />
                    </div>

                    <div class="cart-control-mobile-container">
                        <CartControl
                            v-if="
                                product.stock?.in_stock &&
                                !product.has_attribute_terms
                            "
                            :minValue="1"
                            variant="secondary"
                            @onClickCart="onAddToCart"
                            @onUpdateAmount="onUpdateAmount"
                            :disabledColor="
                                !product.stock?.in_stock &&
                                !product.has_attribute_terms
                            "
                            :loading="loading"
                            colorLoading="primary"
                        />
                        <CartControl
                            v-else
                            variant="secondary"
                            @onClickCart="showProduct"
                            @onUpdateAmount="onUpdateAmount"
                            :minValue="1"
                            :loading="loading"
                            colorLoading="primary"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style>
.card_product_image {
    @apply tw-bg-no-repeat tw-aspect-product-card-image tw-relative;
}

.card-product-container:hover .button_card_product {
    @apply !tw-inline hover:tw-text-primary;
    transition: all 0.2s ease;
}

.button_card_product:hover {
    transform: scale(0.95);
    transition: all 0.2s ease;
}

.card-product-container:hover .q-carousel__slides-container .q-panel {
    overflow: hidden;
}

.card-product-container:hover .card_product_image {
    transform: scale(1.1);
    transition: all 0.5s ease;
    transform-origin: center center;
}

/* Mobile Styles - Only for screens smaller than md (768px) */
@media (max-width: 767px) {
    /* CountDown mobile styles */
    .countdown-mobile-container :deep(.q-field__control) {
        width: 159px !important;
        height: 30px !important;
        min-height: 30px !important;
        border-radius: 3px !important;
        border: 2px solid #ffa380 !important;
        padding: 2px 6px !important;
        background: #ffffff !important;
    }

    .countdown-mobile-container :deep(.q-field__native) {
        padding: 0 !important;
        font-size: 12px !important;
    }

    /* CartControl button mobile styles */
    .cart-control-mobile-container :deep(button) {
        width: 164px !important;
        height: 40px !important;
        border-radius: 50px !important;
        background: #ff5b00 !important;
        font-size: 14px !important;
        padding: 8px 16px !important;
    }

    .cart-control-mobile-container :deep(.q-btn) {
        width: 164px !important;
        height: 40px !important;
        min-height: 40px !important;
    }
}
</style>
